<template>
  <template v-for="(item, index) in items" :key="index">
    <!-- Main table row -->
    <tr 
      :class="[
        { 'cursor-pointer hover:bg-slate-50': clickable },
        { 'border-b-0': expandedRows[index] }
      ]"
      @click="onSelect(index)"
      @mouseenter="onHoverRow?.(index)"
    >
      <!-- Expand/Collapse button column -->
      <td class="px-3 py-4 text-sm font-medium text-center whitespace-nowrap w-12">
        <button
          class="p-2 rounded-full transition-colors duration-200"
          :aria-label="expandedRows[index] ? 'Collapse row' : 'Expand row'"
          @click.stop="toggleRow(index)"
        >
          <span 
            :class="[
              'size-6 transition-transform duration-200 text-slate-600',
              expandedRows[index] ? 'i-heroicons-chevron-up' : 'i-heroicons-chevron-down'
            ]"
          />
        </button>
      </td>
      
      <!-- Main row content slot -->
      <slot name="main-row" :item="item" :index="index" :is-expanded="expandedRows[index]" />
    </tr>

    <!-- Expandable content row -->
    <tr 
      v-if="expandedRows[index]"
      class="bg-slate-50"
    >
      <td></td> <!-- Empty cell for expand button column -->
      <td :colspan="colspan - 1" class="px-4 py-6">
        <div class="transition-all duration-300 ease-in-out">
          <slot name="expandable-content" :item="item" :index="index" />
        </div>
      </td>
    </tr>
  </template>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'

interface Props {
  items: any[]
  colspan: number
  striped?: boolean
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  striped: false,
  clickable: false
})

const emit = defineEmits<{
  'row-click': [index: number, item: any]
  'row-hover': [index: number, item: any]
}>()

const expandedRows = ref<Record<number, boolean>>({})

const toggleRow = (index: number) => {
  expandedRows.value[index] = !expandedRows.value[index]
}

const onSelect = (index: number) => {
  if (props.clickable) {
    emit('row-click', index, props.items[index])
  }
}

const onHoverRow = (index: number) => {
  emit('row-hover', index, props.items[index])
}

// Expose methods for external control
defineExpose({
  expandRow: (index: number) => {
    expandedRows.value[index] = true
  },
  collapseRow: (index: number) => {
    expandedRows.value[index] = false
  },
  toggleRow,
  expandedRows: computed(() => expandedRows.value)
})
</script> 